import { makeStyles, TextField, TextFieldProps } from '@material-ui/core';
import { ClearOutlined } from '@material-ui/icons';
import { useState } from 'react';
import CurrencyTextField from '@unicef/material-ui-currency-textfield';
import React, {
  forwardRef,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import Keyboard, {
  KeyboardOptions,
  KeyboardReactInterface
} from 'react-simple-keyboard';

type KeyboardOption = KeyboardOptions & { enable: boolean };

interface CurrentInputProps {
  handleValueChange(value: any): void;
  value: any;
  minimumValue?: string;
  keyboard?: boolean | KeyboardOption;
  textField?: boolean;
}

const useStyles = makeStyles(() => ({
  keyBoardCurrency: {
    '& input': {
      paddingRight: 25
    }
  },
  wrapper: {
    position: 'relative',
    display: 'flex',
    width: 'fit-content'
  },
  icon: {
    position: 'absolute',
    right: 0,
    bottom: 7,
    opacity: 0.5,
    '&:hover': {
      cursor: 'pointer',
      opacity: 1
    }
  }
}));

const KeyboardField = forwardRef<any, TextFieldProps & CurrentInputProps>(
  ({ handleValueChange, keyboard, textField, ...props }, ref) => {
    const classes = useStyles();
    const value = props.value;
    const keyboardRef = useRef<KeyboardReactInterface | null>();
    const handleOnKeyDown = (
      event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (event.key === 'Tab') {
        return;
      }
      event.preventDefault();
      if (event.key === 'Backspace') {
        if (value > 0.01) {
          handleValueChange(Math.floor(value * 10) / 100);
        } else {
          handleValueChange(0);
        }
      } else {
        const digit = Number(event.key);
        if (!isNaN(digit)) {
          handleValueChange((value * 1000 + digit) / 100);
        }
      }
    };
    const maximumValue = Number.MAX_VALUE.toLocaleString('fullwide', {
      useGrouping: false
    });
    const [error, setError] = useState<string>('');
    const handleMaximumValue = (value: string) => {
      if (value.length > maximumValue.length - 1) {
        setError('Reached limited maximum value');
        return false;
      }
      !error && setError('');
      return true;
    };

    useImperativeHandle(ref, () => ({
      clearInput() {
        keyboardRef.current?.clearInput();
      }
    }));

    useEffect(() => {
      if (value && (keyboard as KeyboardOption)?.enable) {
        setTimeout(
          () => keyboardRef.current?.setInput((value * 100).toString()),
          100
        );
      }
      // eslint-disable-next-line
    }, [(keyboard as KeyboardOption)?.enable]);

    const isKeyboard =
      typeof keyboard === 'boolean' ? keyboard : keyboard?.enable;

    if (textField) {
      return (
        <>
          <TextField
            {...props}
            value={value}
            onChange={(v: any) => handleValueChange(v)}
          />
          {isKeyboard ? (
            <Keyboard
              keyboardRef={r => (keyboardRef.current = r)}
              {...keyboard}
              onChange={(v: any) => handleValueChange(v)}
            />
          ) : null}
        </>
      );
    }

    return (
      <>
        <div className={classes.wrapper}>
          <CurrencyTextField
            {...props}
            className={classes.keyBoardCurrency}
            value={value}
            onChange={(_: any, v: number) => {
              if (handleMaximumValue(v.toString())) {
                handleValueChange(v);
              }
            }}
            maximumValue={maximumValue}
            onKeyDown={handleOnKeyDown}
            error={!!error}
            helperText={error}
            textAlign="left"
          />
          <div
            className={classes.icon}
            onClick={() => {
              handleValueChange(0);
              keyboardRef.current?.clearInput();
            }}
          >
            <ClearOutlined fontSize="small" />
          </div>
        </div>

        {isKeyboard ? (
          <Keyboard
            keyboardRef={r => (keyboardRef.current = r)}
            {...keyboard}
            onChange={(v: string) => {
              if (handleMaximumValue(v)) {
                const value = Math.floor(parseInt(v)) / 100;
                handleValueChange(value);
              }
            }}
          />
        ) : null}
      </>
    );
  }
);

export default KeyboardField;
